import { useQuery, useMutation } from '@apollo/client';
import { Badge } from 'components/Badge/Badge';
import { Button } from 'components/Button/Button';
import { Container } from 'components/Container/Container';
import { EmptyState } from 'components/EmptyState/EmptyState';
import { Input } from 'components/Input/Input';
import { Loader } from 'components/Loader/Loader';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/Table/Table';
import React, { useState } from 'react';
import { MEMBER_TAGS, MEMBER_TAG_CREATE } from 'services/tags';

export const TagsList: React.FC = () => {
    const [newTagName, setNewTagName] = useState('');
    const [newTagCategory, setNewTagCategory] = useState('');
    const { data, loading, refetch } = useQuery(MEMBER_TAGS);

    const [createTag, { loading: creating }] = useMutation(MEMBER_TAG_CREATE, {
        onCompleted: () => {
            setNewTagName('');
            setNewTagCategory('');
            refetch();
        },
    });

    const handleCreateTag = () => {
        if (newTagName.trim()) {
            createTag({
                variables: {
                    name: newTagName,
                    category: newTagCategory || null,
                },
            });
        }
    };

    if (loading) return <Loader />;

    const tags = data?.memberTags || [];

    return (
        <Container>
            <PageTitle>Member Tags</PageTitle>

            <div className="mb-6 p-4 bg-white rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Create New Tag</h3>
                <div className="flex gap-3">
                    <Input
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        placeholder="Tag name (e.g., 'CTO', 'Marketing')"
                        disabled={creating}
                    />
                    <Input
                        value={newTagCategory}
                        onChange={(e) => setNewTagCategory(e.target.value)}
                        placeholder="Category (optional)"
                        disabled={creating}
                    />
                    <Button onClick={handleCreateTag} disabled={creating || !newTagName.trim()}>
                        {creating ? 'Creating...' : 'Create Tag'}
                    </Button>
                </div>
            </div>

            {tags.length === 0 ? (
                <EmptyState title="No tags found" text="Create your first tag using the form above." />
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Usage</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {tags.map((tag: any) => (
                                <TableRow key={tag.id}>
                                    <TableCell>
                                        <span className="font-medium">{tag.name}</span>
                                    </TableCell>
                                    <TableCell>{tag.category || '-'}</TableCell>
                                    <TableCell>{tag.usageCount}</TableCell>
                                    <TableCell>
                                        {tag.isCurated && (
                                            <Badge variant="default">Curated</Badge>
                                        )}
                                        {!tag.isCurated && tag.isApproved && (
                                            <Badge variant="secondary">Approved</Badge>
                                        )}
                                        {!tag.isCurated && !tag.isApproved && (
                                            <Badge variant="outline">Pending</Badge>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </Container>
    );
};
